import * as Typography from "../../Element/Typography";
import Card from "../Card";
import { VariantName } from "../Card/cardConfig";
import LatestArticle from "../LatestArticle/LatestArticle";
import style from "./index.module.css";
import RelatedArticleItem, { RelatedArticleData } from "./RelatedArticleItem";

const { Heading } = Typography;

export type RelatedArticleProps = {
  /**
   * Related stories to display. Sample object in array:
   * {id: 1, title: "I am title", url: "https://www.mywebsite.com", imgUrl: "https://placehold.co/600x400"},
   */
  stories: RelatedArticleData[];
  /**
   * This option controls the visibility of the image thumbnail for the entire component,
   * allowing only text to be displayed. It supersedes the logic determining if a related story
   * has a featured image. Currently, HerWorld has requested this option to completely hide
   * images, leaving only the text visible.
   */
  displayImage: boolean;
  imageErrorSrc?: string;
  headerText?: string;
  headerTextZH?: string;
  hasRelatedPrefix?: boolean;
  htmlAnchor?: string;
  asLatestTeaser?: boolean;
  cardLayout?: {
    defaultVariant: VariantName;
    mobileUp: {
      defaultVariant: VariantName;
    };
  };
};

/**
 * This is our related article component. There are different options:
 * - Card (if cardLayout has VariantName e.g. "card-list-original")
 * - LatestArticle (if asLatestTeaser is TRUE)
 * - RelatedArticleItem (Default)
 */
const RelatedArticle = ({
  stories,
  displayImage = true,
  hasRelatedPrefix = true,
  asLatestTeaser = false,
  cardLayout,
  headerText,
  headerTextZH,
  htmlAnchor,
  imageErrorSrc,
}: RelatedArticleProps): React.ReactElement => {
  const Element = (item: RelatedArticleData) => {
    if (cardLayout) {
      return (
        <Card
          {...item}
          variant={cardLayout.defaultVariant}
          mobileUpVariant={cardLayout.mobileUp.defaultVariant}
          sections={item.sections ?? []}
          path={item.url}
          image={{
            crops: item.image?.crops ?? {},
            alt: item.title,
          }}
          // Related Article titles should not be recognised as Headers
          CustomHeadingElement="div"
        />
      );
    }

    // TODO - Only Fzine is using this:
    // - To move to the Card component
    // - To remove asLatestTeaser prop
    if (asLatestTeaser) {
      return (
        <div className={style.teaser}>
          <LatestArticle
            {...item}
            mode="landscape"
            sections={item.sections ?? []}
            path={item.url}
            image={{
              crops: {
                // Fzine is passing a Cue portrait image to LatestArticle landscape configuration
                landscape: item.image?.crops?.portrait,
              },
              alt: item.title,
            }}
            // Related Article titles should not be recognised as Headers
            CustomHeadingElement="div"
          />
        </div>
      );
    }

    return (
      <RelatedArticleItem
        {...item}
        displayImage={displayImage}
        hasRelatedPrefix={hasRelatedPrefix}
        imageErrorSrc={imageErrorSrc}
      />
    );
  };

  return (
    <div className={style.relatedArticle} id={htmlAnchor}>
      {headerText && (
        <div className={style.headerText}>
          {headerTextZH ? (
            <Heading.SubHeading3 nameZH={headerTextZH}>
              {headerText}
            </Heading.SubHeading3>
          ) : (
            headerText
          )}
        </div>
      )}

      {stories.map((item, index) => (
        <Element key={index} {...item} />
      ))}
    </div>
  );
};

export default RelatedArticle;
